import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUpdateEvent, useEventTypes } from '../hooks/useEvents'
import { useMe } from '../hooks/useMe'
import { ApiClient } from '../api'
import { ManagerPicker } from './ManagerPicker'
import { StationLocationField } from './StationLocationField'
import { MultiDatePicker } from './MultiDatePicker'
import {
  ModalShell, Field, inputClass, ServerError, CancelButton, PrimaryButton
} from './modal-form'
import type { EventListItem, EventStatus, EventUpdate } from '../types'

// Centred modal for editing an event's basic fields. Visual pattern lives in
// modal-form.tsx (ModalShell + Field + inputClass) so every editor in the app
// shares one source of truth.
//
// The modal is mounted/unmounted by the parent (`{open && <EditEventModal ...>}`)
// so each open is a fresh form initialised from the current event prop. The
// prop change after a successful save flows through TanStack Query - the
// useUpdateEvent hook writes the new event into the per-id cache and the
// EventDetail page re-renders with the updated values; if the user reopens
// the modal it re-initialises from those new values.

interface Props {
  event: EventListItem
  onClose: () => void
}

const STATUS_OPTIONS: EventStatus[] = ['Draft', 'In Development', 'Live', 'Complete', 'Cancelled']

export function EditEventModal({ event, onClose }: Props) {
  const updateEvent = useUpdateEvent(event.id)
  // Event type is editable here now (item: tag an event's type from Edit).
  // Cached for an hour by useEventTypes, so it's usually instant.
  const { data: types } = useEventTypes()

  // Form state. Numeric and nullable text fields are held as strings here so
  // the inputs stay controlled (empty input <-> empty string). We coerce to
  // number/null at submit time.
  const [name, setName] = useState(event.name)
  const [status, setStatus] = useState<EventStatus>(event.status)
  const [eventTypeId, setEventTypeId] = useState<number>(event.type.id)
  // Multi-date (item 7): edit the day set; start/end are the derived envelope.
  const [dates, setDates] = useState<string[]>(event.dates ?? [])
  const sortedDates = [...dates].sort()
  const startDate = sortedDates[0] ?? ''
  const endDate = sortedDates[sortedDates.length - 1] ?? ''
  const [location, setLocation] = useState(event.location ?? '')
  const [capacity, setCapacity] = useState(event.capacity != null ? String(event.capacity) : '')
  const [description, setDescription] = useState(event.description ?? '')
  const [progress, setProgress] = useState(String(event.progress))
  const [managerUserId, setManagerUserId] = useState<number | null>(event.manager?.id ?? null)
  // Initial state: '' (empty string) means "no binding". Pre-populated
  // from the event's current MERAC binding (if any) so the user sees
  // their existing selection.

  // MERAC codes load lazily, admin-only. Always include the event's
  // CURRENT code in the dropdown — if it's somehow not in the active
  // catalog (e.g. been deactivated since binding), we still want the
  // user to be able to see "this is what's set" rather than the field
  // silently rendering blank.
  const { data: me } = useMe()
  const { data: meracCodes } = useQuery({
    queryKey: ['merac-codes', { active: true }],
    queryFn: () => ApiClient.meracEventCodes({ active: 'true' }),
    enabled: !!me?.isAdmin,
    staleTime: 5 * 60_000
  })

  // Local validation, mirroring the server. Cheap to compute on every render.
  const trimmedName = name.trim()
  const capacityNum = capacity === '' ? null : Number(capacity)
  const progressNum = Number(progress)

  const fieldErrors: Partial<Record<keyof EventUpdate, string>> = {}
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 255) fieldErrors.name = 'Name must be 255 characters or fewer'
  if (dates.length === 0) fieldErrors.dates = 'Pick at least one date'
  if (capacity !== '' && (capacityNum === null || !Number.isInteger(capacityNum) || capacityNum < 0)) {
    fieldErrors.capacity = 'Capacity must be a whole number (or leave blank)'
  }
  if (!Number.isInteger(progressNum) || progressNum < 0 || progressNum > 100) {
    fieldErrors.progress = 'Progress must be a whole number between 0 and 100'
  }
  const isValid = Object.keys(fieldErrors).length === 0
  const saving = updateEvent.isPending

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: EventUpdate = {
      name: trimmedName,
      status,
      eventTypeId,
      startDate,
      endDate,
      dates: sortedDates,
      location: location.trim() === '' ? null : location.trim(),
      capacity: capacityNum,
      description: description.trim() === '' ? null : description,
      progress: progressNum,
      managerUserId
    }
    // MERAC pairing is now managed in the Sync Hub Ticketing tab, not here — the
    // body omits meracEventCode entirely so the server always leaves the existing
    // binding alone (the field below is read-only).
    updateEvent.mutate(body, {
      onSuccess: () => onClose()
      // onError keeps the modal open so the user can see the error and retry.
    })
  }

  return (
    <ModalShell
      title="Edit event"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Event name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              autoFocus
              className={inputClass(!!fieldErrors.name)}
            />
          </Field>

          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as EventStatus)}
              className={inputClass(false)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>

          <Field label="Event type" hint="Sets the category and the colour shown across the app.">
            <select
              value={eventTypeId}
              onChange={(e) => setEventTypeId(Number(e.target.value))}
              className={inputClass(false)}
              disabled={saving}
            >
              {/* Fall back to the current type while the catalogue loads so the
                  field never renders blank. */}
              {!types?.some((t) => t.id === event.type.id) && (
                <option value={event.type.id}>{event.type.name}</option>
              )}
              {(types ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>

          <ManagerPicker
            value={managerUserId}
            onChange={setManagerUserId}
            disabled={saving}
          />

          {/* MERAC pairing is READ-ONLY here — it's now managed centrally in the
              Sync Hub Ticketing tab (single source of truth). Shows the current
              binding for reference; admins re-pair in the Sync Hub. */}
          {me?.isAdmin && (
            <Field
              label="MERAC event code"
              hint="Pairing is managed in the Sync Hub Ticketing tab (read-only here)."
            >
              <div className={inputClass(false) + ' bg-paper text-ink-soft'} aria-readonly>
                {event.meracEventCode
                  ? `${event.meracEventCode}${
                      meracCodes?.find((c) => c.code === event.meracEventCode)?.description
                        ? ` — ${meracCodes.find((c) => c.code === event.meracEventCode)!.description}`
                        : ''
                    }`
                  : '— Not paired —'}
              </div>
            </Field>
          )}

          <Field
            label="Event dates"
            error={fieldErrors.dates}
            hint="Pick every day the event runs — they can be non-consecutive and span months."
          >
            <MultiDatePicker value={dates} onChange={setDates} disabled={saving} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <StationLocationField value={location} onChange={setLocation} disabled={saving} />
            <Field label="Capacity" error={fieldErrors.capacity} hint="Leave blank for no cap">
              <input
                type="number"
                min="0"
                step="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="—"
                className={inputClass(!!fieldErrors.capacity)}
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className={`${inputClass(false)} resize-y font-body`}
              placeholder="Describe the event so volunteers and managers know what they're building toward."
            />
          </Field>

          <Field label="Build progress" error={fieldErrors.progress} hint={`${progressNum || 0}%`}>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={progress}
              onChange={(e) => setProgress(e.target.value)}
              className={inputClass(!!fieldErrors.progress)}
            />
            <div className="h-1.5 bg-parchment rounded overflow-hidden mt-2">
              <div
                className="h-full bg-green transition-all"
                style={{
                  width:
                    Number.isFinite(progressNum) && progressNum >= 0 && progressNum <= 100
                      ? `${progressNum}%`
                      : '0%'
                }}
              />
            </div>
          </Field>

          {updateEvent.isError && (
            <ServerError title="Could not save changes" error={updateEvent.error as Error} />
          )}
        </div>
      }
    />
  )
}
